import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import './Pages.css';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    
    const { resetPassword } = useAuth();
    const { showToast } = useToast();

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!email) {
            showToast('Please enter your email address', 'error');
            return;
        }

        setLoading(true);

        try {
            await resetPassword(email);
            setEmailSent(true);
            showToast('Password reset email sent! Check your inbox.', 'success');
        } catch (error) {
            console.error('Password reset error:', error);
            
            let errorMessage = 'Failed to send reset email. Please try again.';
            
            switch (error.code) {
                case 'auth/user-not-found':
                    errorMessage = 'No account found with this email address.';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address.';
                    break;
                case 'auth/too-many-requests':
                    errorMessage = 'Too many requests. Please try again later.';
                    break;
                case 'auth/network-request-failed':
                    errorMessage = 'Network error. Please check your connection.';
                    break;
                default:
                    errorMessage = error.message;
            }
            
            showToast(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-container">
                <div className="auth-header">
                    <h1 className="auth-title">Reset Password</h1>
                    <p className="auth-subtitle">
                        {emailSent 
                            ? 'Check your email for reset instructions' 
                            : 'Enter your email to receive password reset instructions'}
                    </p>
                </div>

                {emailSent ? (
                    <div className="forgot-password-success">
                        <div className="success-icon">
                            <i className="fas fa-envelope-open-text"></i>
                        </div>
                        <p className="success-message">
                            We've sent a password reset email to <strong>{email}</strong>
                        </p>
                        <p className="success-instructions">
                            Please check your inbox and follow the instructions to reset your password.
                            If you don't see the email, check your spam folder.
                        </p>
                        <div className="success-actions">
                            <button 
                                className="auth-btn" 
                                onClick={() => setEmailSent(false)}
                                style={{ marginBottom: '10px' }}
                            >
                                <i className="fas fa-redo-alt"></i> Try Again
                            </button>
                            <Link to="/login" className="auth-link">
                                <i className="fas fa-arrow-left"></i> Back to Login
                            </Link>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="auth-form-group">
                            <label htmlFor="email">Email Address</label>
                            <div className="auth-input-with-icon">
                                <i className="fas fa-envelope input-icon"></i>
                                <input
                                    type="email"
                                    id="email"
                                    className="auth-form-control with-icon"
                                    placeholder="Enter your email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                            <p className="input-hint">
                                We'll send a password reset link to this email
                            </p>
                        </div>

                        <button 
                            type="submit" 
                            className="auth-btn" 
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-paper-plane"></i>
                                    Send Reset Email
                                </>
                            )}
                        </button>

                        <div className="auth-footer">
                            <Link to="/login" className="auth-link">
                                <i className="fas fa-arrow-left"></i> Back to Login
                            </Link>
                            <span className="auth-separator">|</span>
                            <Link to="/register" className="auth-link">
                                Create New Account
                            </Link>
                        </div>
                    </form>
                )}

                {/* Security Note */}
                <div className="auth-security-note">
                    <i className="fas fa-shield-alt"></i>
                    <p>
                        For security reasons, password reset links expire after 1 hour.
                        If you don't receive the email within a few minutes, check your spam folder.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
